import { memo, useCallback, useEffect, useRef } from 'react';
import QRCode from 'react-qr-code';

import { useTrackMobileAppQrCodeShown } from '@air/analytics';
import { CheckIcon, CopyIcon } from '@air/design-icons';
import { Button } from '@air/primitive-button';
import { Modal, ModalCloseButton, ModalDescription, ModalTitle } from '@air/primitive-modal';
import { Spinner } from '@air/primitive-spinner';

import { useCopyLinkToClipboard } from '~/hooks/useCopyLinkToClipboard';
import { useMobileHandoffUrl } from '~/hooks/useMobileHandoffUrl';

const DIALOG_TITLE_ID = 'open-on-phone-modal-title';
const DIALOG_DESCRIPTION_ID = 'open-on-phone-modal-description';
const QR_CODE_SIZE = 148;

export const OpenOnPhoneModal = memo(({ onClose }: AirModalProps) => {
  const { copyLinkToClipboard, copied } = useCopyLinkToClipboard();
  const { trackMobileAppQrCodeShown } = useTrackMobileAppQrCodeShown();
  const handoffUrl = useMobileHandoffUrl();
  const hasTrackedQrShownRef = useRef<boolean>();

  useEffect(() => {
    if (handoffUrl && !hasTrackedQrShownRef.current) {
      hasTrackedQrShownRef.current = true;
      trackMobileAppQrCodeShown({ location: 'help-menu' });
    }
  }, [handoffUrl, trackMobileAppQrCodeShown]);

  const handleCopyLink = useCallback(() => {
    if (!handoffUrl) {
      return;
    }

    // biome-ignore lint/nursery/noFloatingPromises: PLA-1724 — pre-existing debt, not introduced by the icon migration
    copyLinkToClipboard(handoffUrl);
  }, [copyLinkToClipboard, handoffUrl]);

  return (
    <Modal
      aria-describedby={DIALOG_DESCRIPTION_ID}
      aria-labelledby={DIALOG_TITLE_ID}
      isOpen
      onDismiss={onClose}
      size="medium"
    >
      <div className="flex items-center justify-between gap-3">
        <ModalTitle id={DIALOG_TITLE_ID}>Open in mobile app</ModalTitle>
        <ModalCloseButton onClick={onClose} />
      </div>

      <ModalDescription className="mt-4" id={DIALOG_DESCRIPTION_ID}>
        Scan this code with your phone camera to open Air on your mobile device.
      </ModalDescription>

      <div className="mt-6 flex flex-col items-center gap-3 rounded-8 border ads:border-ads-gray-5 border-gray-5 ads:bg-base bg-gray-1 p-5">
        {handoffUrl ? (
          <div className="rounded-4 bg-white p-3">
            <QRCode
              aria-hidden
              bgColor="#ffffff"
              fgColor="#000000"
              level="M"
              size={QR_CODE_SIZE}
              title="QR code to open Air on your phone"
              value={handoffUrl}
            />
          </div>
        ) : (
          <div className="flex min-h-44 min-w-44 items-center justify-center">
            <Spinner />
          </div>
        )}
      </div>

      <div className="mt-6 flex justify-end">
        <Button
          appearance="outline"
          color="blue"
          data-testid="OPEN_ON_PHONE_MODAL_COPY_LINK_BUTTON"
          disabled={!handoffUrl}
          onClick={handleCopyLink}
          prefix={copied ? <CheckIcon size="sm" /> : <CopyIcon size="sm" />}
        >
          {copied ? 'Link copied' : 'Copy link'}
        </Button>
      </div>
    </Modal>
  );
});

OpenOnPhoneModal.displayName = 'OpenOnPhoneModal';
